import { createRouter, createWebHistory, RouteRecordRaw } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useLoadingStore } from '@/stores/loading'
import AppLayout from '@/components/layout/AppLayout.vue'
import LoginView from '@/views/LoginView.vue'
import NotFoundView from '@/views/NotFoundView.vue'
import EmployeeView from '@/views/EmployeeView.vue'
import { Users, FileText, FolderLock, GraduationCap, Archive, Building2 } from 'lucide-vue-next'
import applicantAccessService from '@/services/applicantAccessService'

// Types
interface NavigationItem {
  name: string
  to: string
  icon: any
}

interface RouteMeta {
  requiresAuth?: boolean
  title?: string
  icon?: any
  adminOnly?: boolean
  ownerOnly?: boolean
  employeeOnly?: boolean
  hideFromSidebar?: boolean
}

// Route Configuration
const routes: RouteRecordRaw[] = [
  {
    path: '/',
    component: AppLayout,
    meta: { requiresAuth: true },
    redirect: '/staff/training-materials',
    children: [
      {
        path: 'staff',
        name: 'staff',
        component: EmployeeView,
        meta: {
          requiresAuth: true,
          title: 'Staff',
          icon: Users,
          adminOnly: true
        }
      },
      {
        path: 'employee',
        redirect: '/staff',
        meta: {
          hideFromSidebar: true
        }
      },
      {
        path: 'applications',
        name: 'applications',
        component: () => import('@/views/ApplicationManagementView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Applications',
          icon: FileText,
          adminOnly: true
        }
      },
      {
        path: 'applications/add-existing',
        name: 'add-existing-application',
        component: () => import('@/views/AddExistingRecordView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Add Existing Record',
          adminOnly: true,
          hideFromSidebar: true
        }
      },
      {
        path: 'training-materials',
        name: 'training-materials',
        component: () => import('@/views/TrainingMaterialsView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Training Materials',
          icon: GraduationCap,
          adminOnly: true
        }
      },
      {
        path: 'former-employees',
        name: 'former-employees',
        component: () => import('@/views/FormerEmployeesView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Former Employees',
          icon: Archive,
          adminOnly: true
        }
      },
      {
        path: 'business-documents',
        name: 'business-documents',
        component: () => import('@/views/BusinessDocumentsView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Business Documents',
          icon: Building2,
          ownerOnly: true
        }
      },
      {
        path: 'staff/training-materials',
        name: 'staff-training-materials',
        component: () => import('@/views/StaffTrainingMaterialsView.vue'),
        meta: {
          requiresAuth: true,
          title: 'Training Materials',
          icon: GraduationCap,
          employeeOnly: true
        }
      },
      {
        path: 'my-documents',
        name: 'my-documents',
        component: () => import('@/views/MyDocumentsView.vue'),
        meta: {
          requiresAuth: true,
          title: 'My Documents',
          icon: FolderLock,
          employeeOnly: true
        }
      },
      {
        path: '/profile',
        name: 'Profile',
        component: () => import('@/views/ProfileView.vue'),
        meta: {
          requiresAuth: true,
          title: 'My Profile',
          hideFromSidebar: true
        }
      }
    ]
  },
  {
    path: '/login',
    name: 'login',
    component: LoginView,
    meta: {
      requiresAuth: false,
      title: 'Login'
    }
  },
  {
    path: '/forgot-password',
    name: 'forgot-password',
    component: () => import('@/views/ForgotPasswordView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Forgot Password'
    }
  },
  {
    path: '/reset-password',
    name: 'reset-password',
    component: () => import('@/views/ResetPasswordView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Reset Password'
    }
  },
  {
    path: '/apply',
    name: 'apply',
    component: () => import('@/views/EmployeeApplicationView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Apply - Jeducare Services',
      hideFromSidebar: true
    }
  },
  {
    path: '/apply/access',
    name: 'apply-access',
    component: () => import('@/views/ApplicantAccessView.vue'),
    meta: {
      requiresAuth: false,
      title: 'Access Your Application',
      hideFromSidebar: true
    }
  },
  {
    path: '/apply/edit',
    name: 'apply-edit',
    component: () => import('@/views/EmployeeApplicationView.vue'),
    props: { mode: 'edit' },
    meta: {
      requiresAuth: false,
      title: 'Edit Your Application',
      hideFromSidebar: true
    }
  },
  {
    path: '/500',
    name: 'server-error',
    component: () => import('@/views/ServerErrorView.vue'),
    meta: { requiresAuth: false }
  },
  {
    path: '/:pathMatch(.*)*',
    name: 'not-found',
    component: NotFoundView,
    meta: {
      requiresAuth: false,
      title: 'Not Found'
    }
  }
]

// Router Instance
const router = createRouter({
  history: createWebHistory(process.env.VUE_APP_BASE_URL),
  routes
})

// Navigation Helper
export const getNavigationItems = (routesList?: RouteRecordRaw[], userType?: string, isOwner?: boolean): NavigationItem[] => {
  const routesToSearch = (routesList && routesList.length > 0) ? routesList : routes
  const layoutRoute = routesToSearch.find(route => route.path === '/')
  if (!layoutRoute?.children) return []

  const normalizedRole = String(userType ?? '').toLowerCase().trim()
  const isAdmin = normalizedRole === 'admin' || normalizedRole === '0'
  const isEmployee = normalizedRole === 'employee' || normalizedRole === '1'

  return layoutRoute.children
    .filter(route => {
      const meta = route.meta as RouteMeta
      // Filter out routes that don't need to be in navigation
      if (!meta?.requiresAuth || route.name === 'not-found' || route.path === '/profile') {
        return false
      }
      // Skip routes explicitly hidden from sidebar
      if (meta?.hideFromSidebar) {
        return false
      }
      // Filter based on user permissions
      if (meta.adminOnly && !isAdmin) {
        return false
      }
      if (meta.ownerOnly && !isOwner) {
        return false
      }
      if (meta.employeeOnly && !isEmployee) {
        return false
      }
      return true
    })
    .map(route => ({
      name: (route.meta as RouteMeta)?.title || String(route.name),
      to: `/${route.path}`,
      icon: (route.meta as RouteMeta)?.icon
    }))
}

// Navigation Guards
router.beforeEach(async (to, from, next) => {
  const authStore = useAuthStore()
  const loadingStore = useLoadingStore()
  const requiresAuth = to.matched.some(record => record.meta.requiresAuth)

  // Start loading when navigating to a new page (but not on initial load)
  if (from.name !== undefined) {
    const pageTitle = (to.meta?.title as string) || 'Page'
    loadingStore.startPageLoading(`Loading ${pageTitle}...`)
  }

  // Always check authentication status first
  const isAuthenticated = await authStore.checkAuth()
  const userType = authStore.currentUser?.user_type_label

  // If inside an iframe and already authenticated, always redirect to the login page
  const isInIframe = typeof window !== 'undefined' && window.self !== window.top
  if (isInIframe && isAuthenticated && to.name !== 'login') {
    loadingStore.stopPageLoading()
    next({ name: 'login' })
    return
  }

  // If user is authenticated and trying to access login page, redirect appropriately
  if (to.name === 'login' && isAuthenticated) {
    loadingStore.stopPageLoading()
    
    if (isInIframe) {
      next()
      return
    }
    
    // Redirect admins to staff page, employees to employee portal dashboard
    if (userType === 'Admin') {
      next({ name: 'staff' })
    } else {
      next({ name: 'staff-training-materials' })
    }
    return
  }

  // Check if applicant edit route has an active applicant session
  if (to.name === 'apply-edit' && !applicantAccessService.hasActiveSession()) {
    loadingStore.stopPageLoading()
    next({ name: 'apply-access' })
    return
  }

  // Check if route requires authentication
  if (requiresAuth && !isAuthenticated) {
    loadingStore.stopPageLoading()
    next({ name: 'login' })
    return
  }

  // Check if route is admin-only and user is not admin
  const routeMeta = to.meta as RouteMeta
  if (routeMeta?.adminOnly && isAuthenticated) {
    if (userType !== 'Admin') {
      loadingStore.stopPageLoading()
      next({ name: 'staff-training-materials' })
      return
    }
  }

  // Check if route is owner-only and user is not owner
  if (routeMeta?.ownerOnly && isAuthenticated) {
    if (!authStore.isOwner) {
      loadingStore.stopPageLoading()
      next({ name: 'staff' })
      return
    }
  }

  // Check if route is employee-only and user is not employee
  if (routeMeta?.employeeOnly && isAuthenticated) {
    if (userType !== 'employee') {
      loadingStore.stopPageLoading()
      next({ name: 'staff' })
      return
    }
  }

  // Handle root path redirect based on user type
  if (to.path === '/' && isAuthenticated) {
    loadingStore.stopPageLoading()
    if (userType === 'Admin') {
      next({ name: 'staff' })
    } else {
      next({ name: 'staff-training-materials' })
    }
    return
  }

  // Handle non-admin users accessing staff page (default redirect)
  if ((to.name === 'staff' || to.name === 'employee') && isAuthenticated && (from.name === 'login' || from.path === '/' || from.name === undefined)) {
    if (userType !== 'Admin') {
      loadingStore.stopPageLoading()
      next({ name: 'staff-training-materials' })
      return
    }
  }

  next()
})

router.afterEach(() => {
  const loadingStore = useLoadingStore()
  setTimeout(() => {
    loadingStore.stopPageLoading()
  }, 500)
})

export default router